import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from "../config/api";
import SearchableDropdown from "./SearchableDropdown";
import "./PersonalAnalysis.css";

/* ─── Sabitler ─── */
const SITE_LABELS = {
  wikipedia_tr: "Vikipedi (TR)", acikders: "Açık Ders", udemy_tr: "Udemy (Türkçe)",
  khanacademy: "Khan Academy TR", wikipedia: "Vikipedi", mit_ocw: "MIT OpenCourseWare",
  coursera: "Coursera", udemy: "Udemy", edx: "edX", brilliant: "Brilliant",
  openstax: "OpenStax", libretexts: "LibreTexts", wikiversity: "Wikiversity",
  geeksforgeeks: "GeeksforGeeks", w3schools: "W3Schools", tutorialspoint: "Tutorialspoint",
  programiz: "Programiz", freecodecamp: "freeCodeCamp", stackoverflow: "Stack Overflow",
  youtube: "YouTube", google: "Google",
};
const SITE_DOMAINS = {
  wikipedia_tr: "tr.wikipedia.org", acikders: "acikders.org.tr", udemy_tr: "udemy.com",
  khanacademy: "tr.khanacademy.org", wikipedia: "en.wikipedia.org", mit_ocw: "ocw.mit.edu",
  coursera: "coursera.org", udemy: "udemy.com", edx: "edx.org", brilliant: "brilliant.org",
  openstax: "openstax.org", libretexts: "libretexts.org", wikiversity: "en.wikiversity.org",
  geeksforgeeks: "geeksforgeeks.org", w3schools: "w3schools.com", tutorialspoint: "tutorialspoint.com",
  programiz: "programiz.com", freecodecamp: "freecodecamp.org", stackoverflow: "stackoverflow.com",
};

function buildWebUrl(site_key, q) {
  const eq = encodeURIComponent(q);
  if (site_key === "wikipedia_tr") return `https://tr.wikipedia.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "wikipedia")    return `https://en.wikipedia.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "wikiversity")  return `https://en.wikiversity.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "udemy_tr")     return `https://www.udemy.com/courses/search/?q=${eq}&lang=tr`;
  if (site_key === "udemy")        return `https://www.udemy.com/courses/search/?q=${eq}`;
  if (site_key === "coursera")     return `https://www.coursera.org/search?query=${eq}`;
  if (site_key === "stackoverflow")return `https://stackoverflow.com/search?q=${eq}`;
  if (site_key === "youtube")      return `https://www.youtube.com/results?search_query=${eq}`;
  if (site_key === "khanacademy")  return `https://tr.khanacademy.org/search?page_search_query=${eq}`;
  if (site_key === "mit_ocw")      return `https://ocw.mit.edu/search/?q=${eq}`;
  if (site_key === "freecodecamp") return `https://www.freecodecamp.org/news/search/?query=${eq}`;
  if (site_key === "geeksforgeeks")return `https://www.geeksforgeeks.org/search/?q=${eq}`;
  const domain = SITE_DOMAINS[site_key];
  return domain
    ? `https://www.google.com/search?q=site:${domain}+${eq}`
    : `https://www.google.com/search?q=${eq}`;
}

const TYPE_ICONS = { tutorial: "📖", article: "📄", documentation: "📋", interactive: "🎮" };
function typeIcon(t) { return TYPE_ICONS[t] || "🔗"; }

function pctColor(v) {
  if (v == null) return "#94a3b8";
  return `hsl(${(Math.max(0, Math.min(100, v)) / 100) * 120},60%,45%)`;
}

function categoryFromPercent(p) {
  const x = Number(p ?? 0);
  if (x >= 90) return { label: "Mükemmel",         cls: "pa-cat-5" };
  if (x >= 75) return { label: "Yeterli",           cls: "pa-cat-4" };
  if (x >= 60) return { label: "Ortalama Üstü",     cls: "pa-cat-3" };
  if (x >= 45) return { label: "Geliştirmeye Açık", cls: "pa-cat-2" };
  return             { label: "İyileştirilmeli",     cls: "pa-cat-1" };
}

function labelForExamType(t) {
  const map = {
    vize: "Ara Sınav", final: "Final", but: "Bütünleme", bütünleme: "Bütünleme",
    quiz: "Kısa Sınav", odev: "Ödev", ödev: "Ödev", pretest: "Ön Test", pro: "Proje",
  };
  return map[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
}

/* ─── ResourceLinks ─── */
function ResourceLinks({ analysis, ytMap }) {
  const videos = analysis?.videos || [];
  const webRes = analysis?.web_resources || [];
  if (!videos.length && !webRes.length) return null;

  const openUrl = (url) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="pa-res-section">
      {videos.length > 0 && (
        <div className="pa-res-group">
          <span className="pa-res-group-label">▶ YouTube</span>
          <div className="pa-res-cards">
            {videos.map((v, i) => {
              /* Canlı analizde taze ytMap; kayıtlı analizde video objesine gömülü veri kullanılır */
              const yt = ytMap?.[v.search_query] || v;
              const url = yt?.video_url
                || `https://www.youtube.com/results?search_query=${encodeURIComponent(v.search_query || v.title || "")}`;
              const title = yt?.title || v.title || "YouTube";
              const thumb = yt?.thumbnail;
              const dur = yt?.duration_sec ? `${Math.round(yt.duration_sec / 60)} dk`
                : v.duration_min ? `${v.duration_min} dk` : null;
              return (
                <a key={`yt-${i}`} href={url} target="_blank" rel="noopener noreferrer"
                   className="pa-res-card pa-res-video" onClick={openUrl(url)}>
                  <div className="pa-res-thumb">
                    {thumb
                      ? <img src={thumb} alt="" loading="lazy" />
                      : <span className="pa-res-play">▶</span>}
                  </div>
                  <div className="pa-res-info">
                    <span className="pa-res-title">{title.length > 55 ? title.slice(0, 55) + "…" : title}</span>
                    <span className="pa-res-meta">
                      {yt?.channel || v.channel || "YouTube"}{dur && ` · ${dur}`}
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
      {webRes.length > 0 && (
        <div className="pa-res-group">
          <span className="pa-res-group-label">🔗 Web Kaynakları</span>
          <div className="pa-res-cards">
            {webRes.map((r, i) => {
              const url = buildWebUrl(r.site_key, r.title || r.description || "");
              return (
                <a key={`web-${i}`} href={url} target="_blank" rel="noopener noreferrer"
                   className="pa-res-card pa-res-web" onClick={openUrl(url)}>
                  <span className="pa-res-icon">{typeIcon(r.type)}</span>
                  <div className="pa-res-info">
                    <span className="pa-res-title">
                      {(r.title || SITE_LABELS[r.site_key] || r.site_key || "Web").slice(0, 40)}
                    </span>
                    <span className="pa-res-meta">{SITE_LABELS[r.site_key] || r.site_key}</span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function InstructorResourceLinks({ resources }) {
  if (!resources?.length) return null;

  return (
    <div className="pa-instructor-resource-section">
      <div className="pa-instructor-resource-heading">
        <span>Öğretim elemanı önerileri</span>
        <span>{resources.length} kaynak</span>
      </div>
      <div className="pa-res-cards">
        {resources.map(resource => (
          <a
            key={resource.id}
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="pa-res-card pa-instructor-resource-card"
          >
            <span className="pa-instructor-resource-icon" aria-hidden="true">
              {resource.resource_type === "youtube" ? "▶" : "↗"}
            </span>
            <span className="pa-res-info">
              <span className="pa-res-title">{resource.title}</span>
              <span className="pa-res-meta">
                {resource.resource_type === "youtube" ? "YouTube" : "Web kaynağı"}
                {resource.description ? ` · ${resource.description}` : ""}
              </span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ─── OutcomeBar ─── */
function OutcomeBar({ oc, aiEntry, instructorResources }) {
  const cat = categoryFromPercent(oc.pct);
  const aiMap = {};
  (aiEntry?.analyses || []).forEach(a => { aiMap[a.outcome_code] = a; });
  const analysis = aiMap[oc.code] || null;
  const ytMap    = aiEntry?._ytMap || {};
  const hasRes   = (analysis?.videos?.length || 0) + (analysis?.web_resources?.length || 0) > 0;
  const instructorRes = (instructorResources || []).filter(resource => resource.outcome_code === oc.code);

  return (
    <div className="pa-oc-item">
      <div className="pa-oc-header">
        <span className="pa-oc-code" title={oc.description || ""}>{oc.code}</span>
        <div className="pa-oc-bar-track">
          <div className="pa-oc-bar-fill"
               style={{ width: `${oc.pct ?? 0}%`, background: pctColor(oc.pct) }} />
        </div>
        <span className={`pa-oc-badge ${cat.cls}`}>
          {oc.pct != null ? oc.pct.toFixed(1) : "—"}
        </span>
        <span className={`pa-oc-label ${cat.cls}`}>{cat.label}</span>
      </div>
      {oc.description && <p className="pa-oc-desc">{oc.description}</p>}
      {oc.contributing_exam_types?.length > 0 && (
        <p className="pa-oc-contrib">
          Katkı: {oc.contributing_exam_types.map(labelForExamType).join(", ")}
        </p>
      )}
      {hasRes && <ResourceLinks analysis={analysis} ytMap={ytMap} />}
      <InstructorResourceLinks resources={instructorRes} />
    </div>
  );
}

/* ─── StudentCard ─── */
function StudentCard({ student, aiEntry, aiPhase, currentAiIdx, myIdx, isExpanded, onToggle, hasSaved, instructorResources }) {
  const overall  = student.overall_post_midterm_pct;
  const cat      = categoryFromPercent(overall);
  const ocList   = student.learning_outcomes || [];
  const poList   = student.program_outcomes  || [];
  const exams    = student.exams || [];
  const weakCount = ocList.filter(o => (o.pct ?? 999) < 65).length;

  /* Sınıf bazlı durum */
  const aiStatus = aiEntry
    ? (aiEntry.error ? "error" : "done")
    : aiPhase === 2 && myIdx === currentAiIdx ? "active"
    : aiPhase === 2 && myIdx < currentAiIdx   ? "done"
    : "idle";

  return (
    <div className={`pa-card ${isExpanded ? "pa-card-open" : ""}`}>
      <div className={`pa-card-strip pa-strip-${aiStatus}`} />
      <button className="pa-card-header" onClick={onToggle}>
        <div className="pa-card-left">
          <span className="pa-sno">{student.student_no}</span>
          <span className="pa-sname">{student.student_name || "—"}</span>
        </div>
        <div className="pa-card-right">
          {hasSaved && <span className="pa-saved-badge">✓ Analiz kayıtlı</span>}
          {weakCount > 0 && <span className="pa-weak-badge">{weakCount} zayıf ÖÇ</span>}
          <span className="pa-overall-wrap"
                title="Sınav türü ağırlıklarına göre hesaplanan genel başarı yüzdesi">
            <span className="pa-overall-label">Genel</span>
            <span className={`pa-score-badge ${cat.cls}`}>
              {overall != null ? overall.toFixed(1) : "—"}
            </span>
          </span>
          <span className="pa-arrow">{isExpanded ? "▲" : "▼"}</span>
        </div>
      </button>

      {isExpanded && (
        <div className="pa-card-body">

          {/* ── Girilen sınavlar ── */}
          {exams.length > 0 && (
            <>
              <div className="pa-section-title">Girilen Sınavlar</div>
              <div className="pa-exam-breakdown">
                {exams.map(ex => {
                  const c = categoryFromPercent(ex.score_pct);
                  return (
                    <div key={ex.exam_id} className="pa-exam-row">
                      <div className="pa-exam-meta">
                        <span className="pa-exam-title-text">
                          {ex.title || labelForExamType(ex.exam_type)}
                        </span>
                        <span className="pa-exam-date-type">
                          {labelForExamType(ex.exam_type)}
                          {ex.date && <> · {ex.date}</>}
                        </span>
                      </div>
                      <div className="pa-oc-bar-track pa-exam-bar">
                        <div className="pa-oc-bar-fill"
                             style={{ width: `${ex.score_pct ?? 0}%`, background: pctColor(ex.score_pct) }} />
                      </div>
                      <span className={`pa-oc-badge ${c.cls}`}>
                        {ex.score_pct != null ? ex.score_pct.toFixed(1) : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── ÖÇ ── */}
          <div className="pa-section-title">Öğrenme Çıktıları ({ocList.length})</div>
          {aiEntry?.status === "loading" && (
            <div className="pa-ai-loading">
              <span className="pa-spinner-blue" /> AI kaynakları yükleniyor…
            </div>
          )}
          {aiEntry?.error && (
            <div className="pa-ai-error">AI yüklenemedi: {aiEntry.error}</div>
          )}
          {ocList.length === 0
            ? <p className="pa-no-data">ÖÇ verisi bulunamadı.</p>
            : (
              <div className="pa-oc-list">
                {ocList.map(oc => (
                  <OutcomeBar
                    key={oc.id || oc.code}
                    oc={oc}
                    aiEntry={aiEntry}
                    instructorResources={instructorResources}
                  />
                ))}
              </div>
            )
          }

          {/* ── PÖÇ ── */}
          {poList.length > 0 && (
            <>
              <div className="pa-section-title pa-section-title-po">
                Program Öğrenme Çıktıları ({poList.length})
              </div>
              <div className="pa-po-grid">
                {poList.map(po => {
                  const c = categoryFromPercent(po.pct);
                  return (
                    <div key={po.id || po.code} className="pa-po-item">
                      <span className="pa-po-code" title={po.description || ""}>{po.code}</span>
                      <div className="pa-oc-bar-track pa-po-bar">
                        <div className="pa-oc-bar-fill"
                             style={{ width: `${po.pct ?? 0}%`, background: pctColor(po.pct) }} />
                      </div>
                      <span className={`pa-oc-badge ${c.cls}`}>
                        {po.pct != null ? po.pct.toFixed(1) : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
}

/* ─── ClassAveragesTab ─── */
function ClassAveragesTab({ classData, examAggregates }) {
  const loList  = classData.class_averages?.learning_outcomes  || [];
  const poList  = classData.class_averages?.program_outcomes   || [];
  const overall = classData.class_averages?.overall_post_midterm_pct;

  if (!loList.length && !poList.length && !examAggregates?.length) {
    return <p className="pa-no-data" style={{ padding: "24px 0", textAlign: "center" }}>
      Sınıf ortalaması verisi bulunamadı.
    </p>;
  }

  return (
    <div className="pa-class-tab">
      {overall != null && (
        <div className="pa-class-overall">
          <span className="pa-class-overall-label">Sınıf Genel Ortalaması</span>
          <span className={`pa-score-badge ${categoryFromPercent(overall).cls}`}>
            {overall.toFixed(1)}
          </span>
        </div>
      )}

      {/* ── Yapılan sınavlar ── */}
      {examAggregates?.length > 0 && (
        <div className="pa-class-section">
          <div className="pa-section-title">Yapılan Sınavlar</div>
          <div className="pa-class-exam-grid">
            {examAggregates.map(ex => {
              const cat = categoryFromPercent(ex.avg_score);
              return (
                <div key={ex.exam_id} className="pa-class-exam-row">
                  <div className="pa-class-exam-left">
                    <span className="pa-class-exam-title">
                      {ex.title || labelForExamType(ex.exam_type)}
                    </span>
                    <div className="pa-class-exam-meta">
                      <span className="pa-exam-chip pa-chip-sm">{labelForExamType(ex.exam_type)}</span>
                      {ex.date && <span className="pa-class-exam-date">{ex.date}</span>}
                      <span className="pa-class-exam-count">{ex.students} öğrenci girdi</span>
                    </div>
                  </div>
                  <div className="pa-class-exam-right">
                    <div className="pa-oc-bar-track pa-class-exam-bar">
                      <div className="pa-oc-bar-fill"
                           style={{ width: `${ex.avg_score ?? 0}%`, background: pctColor(ex.avg_score) }} />
                    </div>
                    <span className={`pa-oc-badge ${cat.cls}`}>
                      {ex.avg_score != null ? ex.avg_score.toFixed(1) : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loList.length > 0 && (
        <div className="pa-class-section">
          <div className="pa-section-title">Öğrenme Çıktıları Ortalaması</div>
          <div className="pa-class-grid">
            {loList.map(o => {
              const cat = categoryFromPercent(o.avg_pct);
              return (
                <div key={o.id} className={`pa-class-row pa-class-row-colored ${cat.cls}`}>
                  <div className="pa-class-row-head">
                    <span className="pa-oc-code">{o.code}</span>
                    <span className="pa-class-desc" title={o.description}>{o.description}</span>
                    <span className={`pa-oc-badge ${cat.cls}`}>
                      {o.avg_pct != null ? o.avg_pct.toFixed(1) : "—"}
                    </span>
                    <span className={`pa-oc-label ${cat.cls}`}>{cat.label}</span>
                    <span className="pa-class-count">{o.student_count} öğr</span>
                  </div>
                  <div className="pa-oc-bar-track pa-class-bar">
                    <div className="pa-oc-bar-fill"
                         style={{ width: `${o.avg_pct ?? 0}%`, background: pctColor(o.avg_pct) }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {poList.length > 0 && (
        <div className="pa-class-section">
          <div className="pa-section-title pa-section-title-po">
            Program Öğrenme Çıktıları Ortalaması
          </div>
          <div className="pa-class-grid">
            {poList.map(o => {
              const cat = categoryFromPercent(o.avg_pct);
              return (
                <div key={o.id} className={`pa-class-row pa-class-row-po pa-class-row-colored ${cat.cls}`}>
                  <div className="pa-class-row-head">
                    <span className="pa-po-code">{o.code}</span>
                    <span className="pa-class-desc" title={o.description}>{o.description}</span>
                    <span className={`pa-oc-badge ${cat.cls}`}>
                      {o.avg_pct != null ? o.avg_pct.toFixed(1) : "—"}
                    </span>
                    <span className="pa-class-count">{o.student_count} öğr</span>
                  </div>
                  <div className="pa-oc-bar-track pa-class-bar">
                    <div className="pa-oc-bar-fill"
                         style={{ width: `${o.avg_pct ?? 0}%`, background: pctColor(o.avg_pct) }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── StepDot ─── */
function StepDot({ num, label, status, sub }) {
  return (
    <div className={`pa-step pa-step-${status}`}>
      <div className="pa-step-dot">
        {status === "done" ? "✓" : status === "active" ? <span className="pa-spinner" /> : num}
      </div>
      <div className="pa-step-text">
        <span className="pa-step-label">{label}</span>
        {sub && <span className="pa-step-sub">{sub}</span>}
      </div>
    </div>
  );
}

/* ─── Main ─── */
export default function PersonalAnalysis() {
  const [courses, setCourses]         = useState([]);
  const [selectionScope, setSelectionScope] = useState("loading");
  const [universityCatalog, setUniversityCatalog] = useState(null);
  const [facultyId, setFacultyId]     = useState("");
  const [programId, setProgramId]     = useState("");
  const [courseId, setCourseId]       = useState("");
  const [analysisOnly, setAnalysisOnly] = useState(false);
  const [analysisCourseSearch, setAnalysisCourseSearch] = useState("");
  const [courseName, setCourseName]   = useState("");
  const [classData, setClassData]     = useState(null);
  const [loading, setLoading]         = useState(false);
  const [loadError, setLoadError]     = useState("");
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState("students"); // "students" | "class"
  const [instructorResources, setInstructorResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourceError, setResourceError] = useState("");
  const [resourceSaving, setResourceSaving] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    outcome_code: "", resource_type: "youtube", title: "", url: "", description: "",
  });

  /* Toplu AI analizi durumu */
  const [aiPhase, setAiPhase]         = useState(0); // 0=idle 1=running 2=running(idx) 3=done
  const [aiResults, setAiResults]     = useState({}); // { student_no: { analyses, _ytMap, error } }
  const [currentAiIdx, setCurrentAiIdx] = useState(0);
  const [savedNos, setSavedNos]       = useState(new Set()); // DB'de kayıtlı student_no'lar
  const isRunning = useRef(false);
  const abortRef  = useRef(false);
  const tabsRef   = useRef(null);

  /* Rektörlük rollerinde fakülte → program → ders kataloğu kullanılır.
     Endpoint bu yetkiyi sunucuda doğrular; diğer kullanıcılar kendi ders
     listelerine geri döner. */
  useEffect(() => {
    const aid = sessionStorage.getItem("akademisyen_id");
    if (!aid) return;
    let cancelled = false;

    const loadOwnCourses = () => {
      fetch(`${API_BASE_URL}/api/akademisyen/courses?akademisyen_id=${aid}`, { credentials: "include" })
        .then(r => r.ok ? r.json() : [])
        .then(d => {
          if (cancelled) return;
          const ownCourses = Array.isArray(d) ? d : [];
          setCourses(ownCourses);
          setSelectionScope("own");

          // Eski katalog yanıtları analiz durumunu içermeyebilir. Her dersin
          // kayıtlı analizlerini kontrol ederek akademisyen görünümündeki
          // "Analiz var" rozetini güvenilir biçimde tamamlarız.
          Promise.all(ownCourses.map(async (course) => {
            if (course.has_midterm_analysis) return course;
            try {
              const response = await fetch(
                `${API_BASE_URL}/api/ai/student-analyses?course_id=${course.id}`,
                { credentials: "include" }
              );
              if (!response.ok) return course;
              const analysisData = await response.json();
              return {
                ...course,
                has_midterm_analysis: Boolean(
                  analysisData.success && analysisData.student_nos?.length
                ),
              };
            } catch {
              return course;
            }
          })).then(enrichedCourses => {
            if (!cancelled) setCourses(enrichedCourses);
          });
        })
        .catch(() => {
          if (!cancelled) setSelectionScope("own");
        });
    };

    fetch(`${API_BASE_URL}/api/rektor/exam-results?akademisyen_id=${aid}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.role_name && Array.isArray(data.programs) && Array.isArray(data.courses)) {
          setUniversityCatalog(data);
          setSelectionScope("university");
          return;
        }
        loadOwnCourses();
      })
      .catch(loadOwnCourses);

    return () => { cancelled = true; };
  }, []);

  const faculties = useMemo(() => {
    const byId = new Map();
    (universityCatalog?.programs || []).forEach(program => {
      if (program.faculty_id != null && !byId.has(String(program.faculty_id))) {
        byId.set(String(program.faculty_id), {
          id: String(program.faculty_id),
          name: program.faculty_name || "Fakülte",
        });
      }
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [universityCatalog]);

  const programs = useMemo(() => (
    (universityCatalog?.programs || [])
      .filter(program => String(program.faculty_id) === String(facultyId))
      .map(program => ({ id: String(program.program_id), name: program.program_name || "Program" }))
      .sort((a, b) => a.name.localeCompare(b.name, "tr"))
  ), [universityCatalog, facultyId]);

  const universityAnalysisCourseCount = useMemo(() => (
    (universityCatalog?.courses || []).filter(course => course.has_midterm_analysis).length
  ), [universityCatalog]);

  const analyzedCourses = useMemo(() => {
    const query = analysisCourseSearch.trim().toLocaleLowerCase("tr-TR");
    return (universityCatalog?.courses || [])
      .filter(course => course.has_midterm_analysis)
      .filter(course => !query || [
        course.course_code,
        course.course_name,
        course.program_name,
        course.faculty_name,
      ].join(" ").toLocaleLowerCase("tr-TR").includes(query))
      .sort((a, b) => `${a.faculty_name || ""} ${a.program_name || ""} ${a.course_code || ""} ${a.course_name || ""}`
        .localeCompare(`${b.faculty_name || ""} ${b.program_name || ""} ${b.course_code || ""} ${b.course_name || ""}`, "tr"));
  }, [universityCatalog, analysisCourseSearch]);

  const availableCourses = useMemo(() => {
    if (selectionScope !== "university") return courses;
    return (universityCatalog?.courses || [])
      .filter(course =>
        String(course.faculty_id) === String(facultyId)
        && String(course.program_id) === String(programId)
        && (!analysisOnly || course.has_midterm_analysis)
      )
      .map(course => ({
        id: course.course_id,
        code: course.course_code,
        name: course.course_name,
        hasMidtermAnalysis: course.has_midterm_analysis,
      }))
      .sort((a, b) => `${a.code || ""} ${a.name || ""}`.localeCompare(`${b.code || ""} ${b.name || ""}`, "tr"));
  }, [selectionScope, courses, universityCatalog, facultyId, programId, analysisOnly]);

  /* Ders değişince sınıf listesini otomatik yükle */
  useEffect(() => {
    if (!courseId) {
      setClassData(null); setLoadError(""); setExpandedStudent(null);
      setAiResults({}); setAiPhase(0); setSavedNos(new Set());
      setInstructorResources([]); setResourceError("");
      return;
    }
    setLoading(true); setLoadError(""); setClassData(null);
    setExpandedStudent(null); setAiResults({}); setAiPhase(0); setSavedNos(new Set());
    isRunning.current = false; abortRef.current = false;

    fetch(
      `${API_BASE_URL}/api/pre-midterm/post-midterm-class-breakdown?course_id=${courseId}`,
      { credentials: "include" }
    )
      .then(r => r.json())
      .then(data => { if (data.error) throw new Error(data.error); setClassData(data); })
      .catch(e => setLoadError(`Veri yüklenemedi: ${e.message}`))
      .finally(() => setLoading(false));

    fetch(`${API_BASE_URL}/api/ai/student-analyses?course_id=${courseId}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        setSavedNos(new Set(d.student_nos || []));
        const loaded = {};
        Object.entries(d.analyses_map || {}).forEach(([sno, analyses]) => {
          loaded[sno] = { analyses: analyses || [], _ytMap: {} };
        });
        if (Object.keys(loaded).length) {
          setAiResults(loaded);
          setAiPhase(3);
        }
      })
      .catch(() => {});

    setResourcesLoading(true);
    setResourceError("");
    fetch(`${API_BASE_URL}/api/course-outcome-resources?course_id=${courseId}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (!data.success) throw new Error(data.error || "Kaynaklar yüklenemedi");
        setInstructorResources(data.resources || []);
      })
      .catch(error => {
        setInstructorResources([]);
        setResourceError(error.message || "Öğretim elemanı kaynakları yüklenemedi.");
      })
      .finally(() => setResourcesLoading(false));
  }, [courseId]);

  /* Tek öğrenci AI analizi */
  const analyzeStudent = useCallback(async (student) => {
    const weakOc = (student.learning_outcomes || []).filter(o => (o.pct ?? 999) < 65);
    if (!weakOc.length) return { analyses: [], _ytMap: {} };

    const res = await fetch(`${API_BASE_URL}/api/ai/student-outcome-resources`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        course_id: courseId,
        course_name: courseName,
        outcomes: weakOc.map(o => ({
          code: o.code, description: o.description, pct: o.pct,
          exam_evidence: o.exam_evidence || [],
        })),
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "AI hatası");

    /* YouTube */
    const queries = [...new Set(
      (data.analyses || []).flatMap(a => (a.videos || []).map(v => v.search_query).filter(Boolean))
    )];
    const _ytMap = {};
    if (queries.length) {
      const ytRes = await Promise.all(
        queries.map(q =>
          fetch(`${API_BASE_URL}/api/youtube/search?q=${encodeURIComponent(q)}&max=1`, { credentials: "include" })
            .then(r => r.json()).then(d => ({ q, video: (d.videos || [])[0] || null }))
            .catch(() => ({ q, video: null }))
        )
      );
      ytRes.forEach(({ q, video }) => { _ytMap[q] = video; });
    }
    return { analyses: data.analyses || [], _ytMap };
  }, [courseId, courseName]);

  /* Toplu AI çalıştır */
  const runTopluAi = useCallback(async (students, existing) => {
    isRunning.current = true;
    abortRef.current  = false;

    for (let i = 0; i < students.length; i++) {
      if (abortRef.current) break;
      const s   = students[i];
      const key = String(s.student_no);
      if (existing[key]) continue;
      setCurrentAiIdx(i);

      let result = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try { result = await analyzeStudent(s); break; }
        catch (e) { if (attempt === 1) result = { error: e.message, analyses: [], _ytMap: {} }; }
      }
      if (result && !result.error) {
        /* Çözümlenmiş YouTube videosunu her video objesine göm ki DB'de kalıcı olsun.
           Akademisyen yeniden analiz etmedikçe bu linkler tekrar yüklemede aynen gelir. */
        const enrichedAnalyses = (result.analyses || []).map(a => ({
          ...a,
          videos: (a.videos || []).map(v => {
            const yt = result._ytMap?.[v.search_query];
            return yt ? {
              ...v,
              video_id:     yt.video_id,
              video_url:    yt.video_url,
              thumbnail:    yt.thumbnail,
              duration_sec: yt.duration_sec,
              title:        yt.title   || v.title,
              channel:      yt.channel || v.channel,
            } : v;
          }),
        }));
        result.analyses = enrichedAnalyses; // state ile DB tutarlı kalsın

        fetch(`${API_BASE_URL}/api/ai/save-student-analysis`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            course_id: courseId,
            student_no: s.student_no,
            student_name: s.student_name,
            analyses: enrichedAnalyses,
          }),
        })
          .then(r => r.json())
          .then(d => { if (d.success) setSavedNos(prev => new Set([...prev, String(s.student_no)])); })
          .catch(() => {});
      }

      setAiResults(prev => ({ ...prev, [key]: result }));
    }

    if (!abortRef.current) setAiPhase(3);
    isRunning.current = false;
  }, [analyzeStudent, courseId]);

  const handleStartAi = useCallback(() => {
    const students = classData?.students || [];
    if (!students.length || isRunning.current) return;

    const hasErrors = students.some(s => aiResults[String(s.student_no)]?.error);

    if (aiPhase === 3 && hasErrors) {
      /* Sadece hatalıları tekrar çalıştır — mevcut başarılı kayıtlara (ve videolarına) dokunma */
      const existing = {};
      Object.entries(aiResults).forEach(([k, v]) => { if (!v.error) existing[k] = v; });
      setAiPhase(2);
      runTopluAi(students, existing);
      return;
    }

    /* Tam (yeniden) analiz — tüm öğrenciler yeniden üretilir, videolar dahil DB'de güncellenir */
    {
      const msg = savedNos.size > 0
        ? `Bu ders için ${savedNos.size} öğrencinin AI analizi zaten veritabanında kayıtlı.\n\nYeniden analiz başlatılırsa mevcut kayıtlar (öneri videolar dahil) yenisiyle güncellenecektir.\n\nDevam etmek istiyor musunuz?`
        : `Toplu AI analizi başlatılacak.\n\nTüm öğrenciler için AI kaynak önerileri üretilecek ve veritabanına kaydedilecektir.\n\nDevam etmek istiyor musunuz?`;
      if (!window.confirm(msg)) return;
    }

    setAiPhase(2);
    setAiResults({});
    setCurrentAiIdx(0);
    runTopluAi(students, {});
  }, [classData, aiPhase, aiResults, runTopluAi, savedNos]);

  const handleCourseChange = (id) => {
    setCourseId(id);
    const c = availableCourses.find(x => String(x.id) === String(id));
    setCourseName(c ? `${c.code} — ${c.name}` : "");
  };

  const handleAnalyzedCourseSelect = (course) => {
    setFacultyId(String(course.faculty_id));
    setProgramId(String(course.program_id));
    setCourseId(String(course.course_id));
    setCourseName(`${course.course_code ? `${course.course_code} — ` : ""}${course.course_name}`);
    setAnalysisOnly(false);
    setAnalysisCourseSearch("");
  };

  const handleFacultyChange = (id) => {
    setFacultyId(id);
    setProgramId("");
    handleCourseChange("");
  };

  const handleProgramChange = (id) => {
    setProgramId(id);
    handleCourseChange("");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const handleResourceFieldChange = (field, value) => {
    setResourceForm(current => ({ ...current, [field]: value }));
  };

  const handleAddInstructorResource = async (event) => {
    event.preventDefault();
    setResourceError("");
    if (!resourceForm.outcome_code || !resourceForm.url.trim()) {
      setResourceError("Öğrenme çıktısı ve bağlantı zorunludur.");
      return;
    }
    try {
      const parsedUrl = new URL(resourceForm.url.trim());
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) throw new Error();
    } catch {
      setResourceError("Geçerli bir http:// veya https:// bağlantısı girin.");
      return;
    }

    setResourceSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/course-outcome-resources`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: courseId, ...resourceForm }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Kaynak kaydedilemedi.");
      setInstructorResources(current => [...current, data.resource]);
      setResourceForm(current => ({
        ...current,
        title: "",
        url: "",
        description: "",
      }));
    } catch (error) {
      setResourceError(error.message || "Kaynak kaydedilemedi.");
    } finally {
      setResourceSaving(false);
    }
  };

  const handleDeleteInstructorResource = async (resource) => {
    if (!window.confirm(`“${resource.title}” kaynağını kaldırmak istiyor musunuz?`)) return;
    setResourceError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/course-outcome-resources/${resource.id}?course_id=${courseId}`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Kaynak silinemedi.");
      setInstructorResources(current => current.filter(item => item.id !== resource.id));
    } catch (error) {
      setResourceError(error.message || "Kaynak silinemedi.");
    }
  };

  const examAggregates = useMemo(() => {
    const map = {};
    (classData?.students || []).forEach(s => {
      (s.exams || []).forEach(ex => {
        if (!map[ex.exam_id]) {
          map[ex.exam_id] = {
            exam_id: ex.exam_id, title: ex.title,
            exam_type: ex.exam_type, date: ex.date,
            students: 0, totalScore: 0, scoreCount: 0,
          };
        }
        map[ex.exam_id].students++;
        if (ex.score_pct != null) {
          map[ex.exam_id].totalScore += ex.score_pct;
          map[ex.exam_id].scoreCount++;
        }
      });
    });
    const parse = d => {
      if (!d) return 0;
      const [day, mo, yr] = d.split(".").map(Number);
      return new Date(yr, mo - 1, day).getTime();
    };
    return Object.values(map)
      .map(e => ({ ...e, avg_score: e.scoreCount > 0 ? e.totalScore / e.scoreCount : null }))
      .sort((a, b) => parse(a.date) - parse(b.date));
  }, [classData]);

  const students = useMemo(() => classData?.students || [], [classData]);
  const examTypes  = Object.keys(classData?.exam_type_weights || {});
  const courseOutcomes = useMemo(() => {
    const byCode = new Map();
    students.forEach(student => {
      (student.learning_outcomes || []).forEach(outcome => {
        if (outcome.code && !byCode.has(outcome.code)) {
          byCode.set(outcome.code, { code: outcome.code, description: outcome.description || "" });
        }
      });
    });
    return [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code, "tr", { numeric: true }));
  }, [students]);
  const doneCount  = Object.keys(aiResults).length;
  const progress   = students.length > 0 ? Math.round((doneCount / students.length) * 100) : 0;
  const curStudent = students[currentAiIdx];

  const canResume = aiPhase === 3 && students.some(s => aiResults[String(s.student_no)]?.error);
  const canStart = students.length > 0
    && aiPhase !== 2
    && (aiPhase !== 3 || canResume);
  const canManageInstructorResources = selectionScope === "own";

  useEffect(() => {
    if (!courseOutcomes.length) return;
    setResourceForm(current => (
      courseOutcomes.some(outcome => outcome.code === current.outcome_code)
        ? current
        : { ...current, outcome_code: courseOutcomes[0].code }
    ));
  }, [courseOutcomes]);

  const st1 = aiPhase === 0 ? "idle" : "done";
  const st2 = aiPhase < 2 ? "idle" : aiPhase === 2 ? "active" : "done";
  const st3 = aiPhase < 3 ? "idle" : "done";
  const sub2 = aiPhase === 2
    ? `${doneCount}/${students.length} — ${curStudent?.student_name || ""}`
    : aiPhase > 2 ? `${doneCount} öğrenci tamamlandı` : null;

  return (
    <div className="pa-wrap">

      {/* Hero */}
      <div className="pa-hero">
        <div className="pa-hero-content">
          <h2 className="pa-hero-title">Arasınav Dönemi Analizi</h2>
          <p className="pa-hero-sub">Zayıf ÖÇ'ler için kişiselleştirilmiş AI kaynak önerileri</p>
        </div>
        <div className="pa-exam-legend">
          <span className="pa-leg-in">✓ Ödev</span>
          <span className="pa-leg-in">✓ Kısa Sınav</span>
          <span className="pa-leg-in">✓ Ön Test</span>
          <span className="pa-leg-in">✓ Ara Sınav</span>
          <span className="pa-leg-out">✗ Final</span>
          <span className="pa-leg-out">✗ Bütünleme</span>
        </div>
      </div>

      {/* Ders seçici */}
      <div className="pa-controls">
        {selectionScope === "university" ? (
          <div className="pa-university-picker">
            <div className="pa-picker-heading">
              <div>
                <span className="pa-picker-eyebrow">Üniversite kapsamı</span>
                <span className="pa-picker-help">Fakülte, program ve ders seçerek analizi görüntüleyin.</span>
              </div>
              <button
                type="button"
                className={`pa-analysis-filter${analysisOnly ? " is-active" : ""}`}
                onClick={() => {
                  setAnalysisOnly(current => !current);
                  setAnalysisCourseSearch("");
                  handleCourseChange("");
                }}
                aria-pressed={analysisOnly}
              >
                {analysisOnly ? "Tüm dersleri görüntüle" : "Analizi olan dersleri görüntüle"}
                <span>{universityAnalysisCourseCount}</span>
              </button>
            </div>
            <div className="pa-picker-grid">
              <div className="pa-picker-field">
                <label htmlFor="pa-faculty">1. Fakülte</label>
                <SearchableDropdown
                  id="pa-faculty"
                  value={facultyId}
                  onChange={handleFacultyChange}
                  disabled={loading || aiPhase === 2}
                  placeholder="— Fakülte seçin —"
                  searchPlaceholder="Fakülte ara…"
                  ariaLabel="Fakülte"
                  options={faculties.map(faculty => ({
                    value: faculty.id,
                    label: faculty.name,
                    searchText: faculty.name,
                  }))}
                />
              </div>
              <div className="pa-picker-field">
                <label htmlFor="pa-program">2. Program</label>
                <SearchableDropdown
                  id="pa-program"
                  value={programId}
                  onChange={handleProgramChange}
                  disabled={!facultyId || loading || aiPhase === 2}
                  placeholder="— Program seçin —"
                  searchPlaceholder="Program ara…"
                  ariaLabel="Program"
                  options={programs.map(program => ({
                    value: program.id,
                    label: program.name,
                    searchText: program.name,
                  }))}
                />
              </div>
              <div className="pa-picker-field">
                <label htmlFor="pa-course">3. Ders</label>
                <SearchableDropdown
                  id="pa-course"
                  value={courseId}
                  onChange={handleCourseChange}
                  disabled={!programId || loading || aiPhase === 2}
                  placeholder="— Ders seçin —"
                  searchPlaceholder="Ders adı veya koduyla ara…"
                  ariaLabel="Ders"
                  options={availableCourses.map(course => ({
                    value: course.id,
                    label: `${course.code ? `${course.code} — ` : ""}${course.name}`,
                    searchText: `${course.code || ""} ${course.name || ""}`,
                    badge: course.hasMidtermAnalysis ? "Analiz var" : undefined,
                  }))}
                />
              </div>
            </div>

            {analysisOnly && (
              <section className="pa-analyzed-courses" aria-labelledby="pa-analyzed-courses-title">
                <div className="pa-analyzed-courses-head">
                  <div>
                    <h3 id="pa-analyzed-courses-title">Analizli dersler</h3>
                    <p>Bir ders seçerek kaydedilmiş arasınav analizini inceleyin.</p>
                  </div>
                  <span className="pa-analyzed-courses-count" aria-live="polite">
                    {analyzedCourses.length} ders
                  </span>
                </div>
                <label className="pa-analyzed-search">
                  <span aria-hidden="true">⌕</span>
                  <input
                    type="search"
                    value={analysisCourseSearch}
                    onChange={(event) => setAnalysisCourseSearch(event.target.value)}
                    placeholder="Ders, program veya fakülte ara…"
                    aria-label="Analizli derslerde ara"
                  />
                </label>
                {analyzedCourses.length > 0 ? (
                  <div className="pa-analyzed-course-list">
                    {analyzedCourses.map(course => (
                      <button
                        type="button"
                        className="pa-analyzed-course"
                        key={course.course_id}
                        onClick={() => handleAnalyzedCourseSelect(course)}
                      >
                        <span className="pa-analyzed-course-title">
                          {course.course_code && <strong>{course.course_code}</strong>}
                          {course.course_name}
                        </span>
                        <span className="pa-analyzed-course-meta">
                          {course.faculty_name} · {course.program_name}
                        </span>
                        <span className="pa-analyzed-course-action">İncele →</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="pa-analyzed-courses-empty">Bu aramayla eşleşen analizli ders yok.</div>
                )}
              </section>
            )}
          </div>
        ) : (
          <SearchableDropdown
            value={courseId}
            onChange={handleCourseChange}
            disabled={loading || aiPhase === 2 || selectionScope === "loading"}
            placeholder="— Ders seçin —"
            searchPlaceholder="Ders adı veya koduyla ara…"
            ariaLabel="Ders"
            options={availableCourses.map(c => ({
              value: c.id,
              label: `${c.code} — ${c.name}`,
              searchText: `${c.code || ''} ${c.name || ''}`,
              badge: c.has_midterm_analysis ? "Analiz var" : undefined,
            }))}
          />
        )}

        {loading && (
          <div className="pa-loading-row">
            <span className="pa-spinner-blue" />
            <span className="pa-loading-text">Öğrenciler yükleniyor…</span>
          </div>
        )}

        {!loading && students.length > 0 && (
          <button
            className={`pa-start-btn ${!canStart ? "pa-btn-disabled" : ""}`}
            disabled={!canStart}
            onClick={handleStartAi}
          >
            {aiPhase === 0 ? "Toplu AI Analizi Başlat"
              : aiPhase === 2 ? "İşleniyor…"
              : canResume ? "Hatalıları Yeniden İşle"
              : "Yeniden Analiz Et"}
          </button>
        )}
      </div>

      {loadError && <div className="pa-error">{loadError}</div>}

      {courseId && canManageInstructorResources && (
        <section className="pa-instructor-manager" aria-labelledby="pa-instructor-manager-title">
          <div className="pa-instructor-manager-head">
            <div>
              <span className="pa-instructor-manager-eyebrow">Öğretim elemanı kaynakları</span>
              <h3 id="pa-instructor-manager-title">ÖÇ'ye kaynak ekleyin</h3>
              <p>Eklediğiniz YouTube ve web kaynakları, ilgili ÖÇ altında AI önerileriyle birlikte görünür.</p>
            </div>
            <span className="pa-instructor-manager-count">{instructorResources.length} kaynak</span>
          </div>

          {resourceError && <div className="pa-resource-error" role="alert">{resourceError}</div>}

          {courseOutcomes.length > 0 ? (
            <form className="pa-resource-form" onSubmit={handleAddInstructorResource}>
              <label className="pa-resource-field pa-resource-outcome">
                <span>Öğrenme çıktısı</span>
                <select
                  value={resourceForm.outcome_code}
                  onChange={(event) => handleResourceFieldChange("outcome_code", event.target.value)}
                  disabled={resourceSaving}
                >
                  {courseOutcomes.map(outcome => (
                    <option key={outcome.code} value={outcome.code}>
                      {outcome.code}{outcome.description ? ` — ${outcome.description}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="pa-resource-field pa-resource-type">
                <span>Kaynak türü</span>
                <select
                  value={resourceForm.resource_type}
                  onChange={(event) => handleResourceFieldChange("resource_type", event.target.value)}
                  disabled={resourceSaving}
                >
                  <option value="youtube">YouTube videosu</option>
                  <option value="web">Web kaynağı</option>
                </select>
              </label>
              <label className="pa-resource-field pa-resource-title">
                <span>Başlık <em>isteğe bağlı</em></span>
                <input
                  value={resourceForm.title}
                  onChange={(event) => handleResourceFieldChange("title", event.target.value)}
                  placeholder="Örn. Elektrik motorlarına giriş"
                  maxLength="240"
                  disabled={resourceSaving}
                />
              </label>
              <label className="pa-resource-field pa-resource-url">
                <span>Bağlantı</span>
                <input
                  type="url"
                  value={resourceForm.url}
                  onChange={(event) => handleResourceFieldChange("url", event.target.value)}
                  placeholder="https://…"
                  required
                  disabled={resourceSaving}
                />
              </label>
              <label className="pa-resource-field pa-resource-note">
                <span>Not <em>isteğe bağlı</em></span>
                <input
                  value={resourceForm.description}
                  onChange={(event) => handleResourceFieldChange("description", event.target.value)}
                  placeholder="Öğrenciye kısa not"
                  maxLength="2000"
                  disabled={resourceSaving}
                />
              </label>
              <button className="pa-resource-add" type="submit" disabled={resourceSaving}>
                {resourceSaving ? "Kaydediliyor…" : "+ Kaynağı ekle"}
              </button>
            </form>
          ) : (
            <p className="pa-resource-empty">Kaynak eklemek için bu derste ÖÇ ve öğrenci verisi bulunmalıdır.</p>
          )}

          {resourcesLoading ? (
            <div className="pa-resource-loading"><span className="pa-spinner-blue" /> Kaynaklar yükleniyor…</div>
          ) : instructorResources.length > 0 ? (
            <div className="pa-resource-list" aria-label="Eklenmiş öğretim elemanı kaynakları">
              {instructorResources.map(resource => (
                <div className="pa-resource-list-item" key={resource.id}>
                  <span className="pa-resource-outcome-code">{resource.outcome_code}</span>
                  <span className={`pa-resource-kind pa-resource-kind-${resource.resource_type}`}>
                    {resource.resource_type === "youtube" ? "YouTube" : "Web"}
                  </span>
                  <a href={resource.url} target="_blank" rel="noopener noreferrer" className="pa-resource-link">
                    {resource.title}
                  </a>
                  {resource.description && <span className="pa-resource-list-note">{resource.description}</span>}
                  <button
                    type="button"
                    className="pa-resource-delete"
                    onClick={() => handleDeleteInstructorResource(resource)}
                    aria-label={`${resource.title} kaynağını sil`}
                  >
                    Sil
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="pa-resource-empty">Henüz öğretim elemanı tarafından eklenmiş bir kaynak yok.</p>
          )}
        </section>
      )}

      {/* Özet şeridi */}
      {classData && students.length > 0 && (
        <div className="pa-summary-bar">
          <span className="pa-sum-item"><strong>{students.length}</strong> öğrenci</span>
          <span className="pa-sum-sep">·</span>
          <span className="pa-sum-item">
            <strong>{[...new Set(students.flatMap(s => (s.learning_outcomes||[]).map(o=>o.code)))].length}</strong> ÖÇ
          </span>
          <span className="pa-sum-sep">·</span>
          <span className="pa-sum-item">
            <strong>{[...new Set(students.flatMap(s => (s.program_outcomes||[]).map(o=>o.code)))].length}</strong> PÖÇ
          </span>
          {examTypes.length > 0 && (
            <>
              <span className="pa-sum-sep">·</span>
              <div className="pa-sum-exams">
                {examTypes.map(t => <span key={t} className="pa-exam-chip">{labelForExamType(t)}</span>)}
              </div>
            </>
          )}
        </div>
      )}

      {/* AI Stepper (sadece başlatılınca göster) */}
      {aiPhase > 0 && (
        <>
          <div className="pa-stepper">
            <StepDot num="1" label="Veri Yüklendi" status={st1}
              sub={`${students.length} öğrenci · ${examTypes.map(labelForExamType).join(", ")}`} />
            <div className="pa-step-line" />
            <StepDot num="2" label="AI Analizi" status={st2} sub={sub2} />
            <div className="pa-step-line" />
            <StepDot num="3" label="Tamamlandı" status={st3}
              sub={st3 === "done" ? `${doneCount} öğrenci işlendi` : null} />
          </div>

          {aiPhase === 2 && (
            <div className="pa-progress-wrap">
              <div className="pa-progress">
                <div className="pa-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="pa-progress-pct">{progress}%</span>
            </div>
          )}
        </>
      )}

      {/* Boş durum */}
      {!loading && !loadError && courseId && students.length === 0 && classData && (
        <div className="pa-empty">Bu ders için öğrenci verisi bulunamadı.</div>
      )}

      {/* Tab bar */}
      {students.length > 0 && (
        <div className="pa-tabs" ref={tabsRef}>
          <button
            className={`pa-tab ${activeTab === "students" ? "pa-tab-active" : ""}`}
            onClick={() => handleTabChange("students")}
          >
            Öğrenciler
            <span className="pa-tab-count">{students.length}</span>
          </button>
          <button
            className={`pa-tab ${activeTab === "class" ? "pa-tab-active" : ""}`}
            onClick={() => handleTabChange("class")}
          >
            Sınıf Ortalaması
            {examAggregates.length > 0 && (
              <span className="pa-tab-count">{examAggregates.length} sınav</span>
            )}
          </button>
        </div>
      )}

      <div className="pa-tab-panel">
        {/* ── Öğrenci listesi tab ── */}
        {activeTab === "students" && students.length > 0 && (
          <>
            <div className="pa-student-list">
              {students.map((s, i) => (
                <StudentCard
                  key={s.student_no}
                  student={s}
                  aiEntry={aiResults[String(s.student_no)] || null}
                  aiPhase={aiPhase}
                  currentAiIdx={currentAiIdx}
                  myIdx={i}
                  isExpanded={expandedStudent === String(s.student_no)}
                  onToggle={() => setExpandedStudent(prev =>
                    prev === String(s.student_no) ? null : String(s.student_no)
                  )}
                  hasSaved={savedNos.has(String(s.student_no))}
                  instructorResources={instructorResources}
                />
              ))}
            </div>
            {aiPhase === 3 && doneCount > 0 && (
              <div className="pa-done-banner">
                Analiz tamamlandı — {doneCount} öğrenci için AI kaynak önerileri hazır.
                Öğrenciye tıklayarak ÖÇ detaylarını görüntüleyin.
              </div>
            )}
          </>
        )}

        {/* ── Sınıf Ortalaması tab ── */}
        {activeTab === "class" && classData && (
          <ClassAveragesTab classData={classData} examAggregates={examAggregates} />
        )}
      </div>
    </div>
  );
}
